import { createContext, useContext, type ReactNode } from 'react';

export interface FunnelAnalytics {
  trackExperienceShown: (params: {
    funnelId: string;
    stepId: string;
    stepName?: string;
    experienceType?: string;
    placement?: string;
  }) => void;
  trackOfferAccepted: (params: {
    funnelId: string;
    stepId: string;
    offerId?: string;
    offerName?: string;
    productId?: string;
  }) => void;
  trackOfferDeclined: (params: { funnelId: string; stepId: string; offerId?: string }) => void;
  trackAddToCart: (params: {
    productId: string;
    variantId?: string;
    quantity: number;
    source: 'funnel_popup' | 'funnel_offer' | 'funnel_free_gift';
  }) => void;
  trackPopupShown: (params: { stepId: string; actionType: string; actionId: string }) => void;
  trackLeadsSubmitted: (params: { stepId: string; actionId: string }) => void;
  trackExperienceDismissed: (params: { funnelId: string; stepId: string }) => void;
}

const noop = () => {};

export const defaultFunnelAnalytics: FunnelAnalytics = {
  trackExperienceShown: noop,
  trackOfferAccepted: noop,
  trackOfferDeclined: noop,
  trackAddToCart: noop,
  trackPopupShown: noop,
  trackLeadsSubmitted: noop,
  trackExperienceDismissed: noop,
};

const FunnelAnalyticsContext = createContext<FunnelAnalytics>(defaultFunnelAnalytics);

export function FunnelAnalyticsProvider({
  value,
  children,
}: {
  value: FunnelAnalytics;
  children: ReactNode;
}) {
  return (
    <FunnelAnalyticsContext.Provider value={value}>{children}</FunnelAnalyticsContext.Provider>
  );
}

export function useFunnelAnalytics(): FunnelAnalytics {
  return useContext(FunnelAnalyticsContext);
}

function mergeAnalytics(partial?: Partial<FunnelAnalytics>): FunnelAnalytics {
  if (!partial) return defaultFunnelAnalytics;
  return { ...defaultFunnelAnalytics, ...partial };
}

export { mergeAnalytics };
